import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { uploadPhoto, verifyAccessCode } from '../services/api';
import { getWebSocketUrl } from '../config';

function HomeownerConsole() {
  const [searchParams] = useSearchParams();
  const roomId = searchParams.get('room');
  
  const [accessCode, setAccessCode] = useState('');
  const [codeVerified, setCodeVerified] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState('');
  const [joined, setJoined] = useState(false);
  const [audioMuted, setAudioMuted] = useState(false);
  const [log, setLog] = useState('');
  const [gpsStatus, setGpsStatus] = useState(null); // null, 'good', 'fair', 'poor'
  const [connectionStatus, setConnectionStatus] = useState('inactive'); // 'active' or 'inactive'
  const [peerPresent, setPeerPresent] = useState(false);
  const peerPresentRef = useRef(false);
  const [sessionEnded, setSessionEnded] = useState(false);
  const [photoCaptureConfirmed, setPhotoCaptureConfirmed] = useState(false);
  
  const localVideoRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const wsRef = useRef(null);
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const pendingCandidatesRef = useRef([]);
  const verifiedAccessCodeRef = useRef(null);

  const addLog = (message) => {
    setLog(prev => prev + message + '\n');
  };

  // Helper function to safely add tracks to peer connection
  const addLocalStreamToPeerConnection = (pc, stream) => {
    if (!pc || !stream) return;
    
    try {
      // Check which tracks are already added
      const existingTracks = new Set();
      pc.getSenders().forEach(sender => {
        if (sender.track) {
          existingTracks.add(sender.track.id);
        }
      });
      
      // Only add tracks that aren't already added
      stream.getTracks().forEach(track => {
        if (!existingTracks.has(track.id)) {
          pc.addTrack(track, stream);
        }
      });
    } catch (e) {
      addLog('Error adding tracks to peer connection: ' + e.message);
    }
  };

  const makePeerConnection = () => {
    // Close existing connection if any
    if (pcRef.current) {
      try {
        pcRef.current.close();
      } catch (e) {
        addLog('Error closing old peer connection: ' + e.message);
      }
    }
    
    // Clear any pending ICE candidates
    pendingCandidatesRef.current = [];
    
    const pc = new RTCPeerConnection({
      iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
    });

    pc.onicecandidate = (e) => {
      if (e.candidate) {
        wsSend({ type: 'signal', roomId, data: { ice: e.candidate } });
      }
    };

    pc.ontrack = (ev) => {
      if (ev.track.kind === 'audio' && remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = ev.streams[0];
        remoteAudioRef.current.muted = false; // Auto-enable audio
        remoteAudioRef.current.play().catch(() => {});
        setAudioMuted(false); // Update state to reflect audio is enabled
        addLog('Remote audio received and enabled.');
      }
    };

    pc.onconnectionstatechange = () => {
      const state = pc.connectionState;
      // Update connection status: active only if peer is present AND connection is established
      // Use a ref to get the current peerPresent value since closure might have stale value
      if (peerPresentRef.current && state === 'connected') {
        setConnectionStatus('active');
      } else if (!peerPresentRef.current || state === 'disconnected' || state === 'failed' || state === 'closed') {
        setConnectionStatus('inactive');
      }
      
      if (state === 'failed') {
        // Try to restart ICE
        pc.restartIce().catch(() => {});
      }
    };
    
    pc.oniceconnectionstatechange = () => {
      const state = pc.iceConnectionState;
      // Update connection status: active only if peer is present AND ICE is connected
      if (peerPresentRef.current && state === 'connected') {
        setConnectionStatus('active');
      } else if (state === 'failed' || state === 'disconnected') {
        setConnectionStatus('inactive');
        // Try to restart ICE
        pc.restartIce().catch(() => {});
      }
    };

    return pc;
  };

  const startLocal = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1200 } },
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
      });
      
      localStreamRef.current = stream;
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
      
      if (pcRef.current) {
        for (const track of stream.getTracks()) {
          pcRef.current.addTrack(track, stream);
        }
      }
      addLog('Local tracks added.');
    } catch (error) {
      addLog('getUserMedia error: ' + error.message);
    }
  };

  const wsSend = (obj) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(obj));
    }
  };

  const sendLocationUpdate = async () => {
    try {
      if (!navigator.geolocation) {
        addLog('Geolocation not available on this device');
        return;
      }
      
      addLog('Getting location to share with appraiser...');
      
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, { 
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 60000 // Allow 1 minute cached location for periodic updates
        });
      }).catch((error) => {
        addLog('Location error: ' + error.message);
        return null;
      });

      if (position && position.coords) {
        const { latitude, longitude, accuracy } = position.coords;
        
        if (!latitude || !longitude) {
          addLog('Invalid location data received');
          return;
        }
        
        wsSend({ 
          type: 'location_update', 
          roomId,
          latitude,
          longitude,
          accuracy: accuracy || 0
        });
        addLog(`✓ Location shared with appraiser: ${latitude.toFixed(4)}, ${longitude.toFixed(4)} (±${Math.round(accuracy)}m)`);
      } else {
        addLog('Could not get location data');
      }
    } catch (e) {
      console.error('Error sending location:', e);
      addLog('Error sending location: ' + e.message);
    }
  };

  const takePhotoAndUpload = async () => {
    try {
      if (!localStreamRef.current) {
        if (!pcRef.current) {
          pcRef.current = makePeerConnection();
        }
        await startLocal();
      }
      
      const track = localStreamRef.current.getVideoTracks()[0];
      let photoBlob = null;

      // Always use canvas to ensure JPEG format with EXIF compatibility
      const canvas = document.createElement('canvas');
      const settings = track?.getSettings ? track.getSettings() : {};
      const w = settings.width || localVideoRef.current?.videoWidth || 1280;
      const h = settings.height || localVideoRef.current?.videoHeight || 720;
      canvas.width = w;
      canvas.height = h;
      canvas.getContext('2d').drawImage(localVideoRef.current, 0, 0, w, h);
      photoBlob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', 0.92));

      const position = await new Promise((resolve, reject) => {
        if (!navigator.geolocation) return resolve({ coords: {} });
        addLog('Acquiring GPS location...');
        navigator.geolocation.getCurrentPosition(resolve, reject, { 
          enableHighAccuracy: true,  // Use GPS hardware, not WiFi/IP
          timeout: 30000,             // 30 seconds to get a good GPS lock
          maximumAge: 0               // Don't use cached location, get fresh GPS
        });
      }).catch((error) => {
        addLog('GPS error: ' + error.message);
        return { coords: {} };
      });

      const { latitude, longitude, accuracy } = position.coords || {};
      
      if (latitude && longitude) {
        if (accuracy) {
          addLog(`GPS acquired: accuracy ±${Math.round(accuracy)}m`);
          // Set GPS status based on accuracy
          if (accuracy < 20) {
            setGpsStatus('good');
          } else if (accuracy < 100) {
            setGpsStatus('fair');
          } else {
            setGpsStatus('poor');
          }
        } else {
          addLog('GPS acquired (accuracy unknown)');
          setGpsStatus('fair');
        }
      } else {
        addLog('Warning: No GPS data available');
        setGpsStatus('poor');
      }
      
      const response = await uploadPhoto(roomId, photoBlob, latitude, longitude, accuracy);
      addLog('Photo + GPS uploaded successfully.');
    } catch (e) {
      addLog('Error capturing/uploading photo: ' + e.message);
      console.error(e);
    }
  };

  const handleVerifyCode = async () => {
    if (!accessCode.trim()) {
      setVerifyError('Please enter the access code');
      return;
    }

    if (!roomId) {
      setVerifyError('Missing room ID');
      return;
    }

    setVerifying(true);
    setVerifyError('');

    try {
      const result = await verifyAccessCode(roomId, accessCode.trim());
      
      if (result.valid) {
        setCodeVerified(true);
        verifiedAccessCodeRef.current = accessCode.trim();
        
        // Store the homeowner JWT token
        if (result.token) {
          localStorage.setItem('homeowner_token', result.token);
          addLog('Access code verified successfully. Token received.');
        } else {
          addLog('Access code verified successfully.');
        }
      } else {
        // Show specific error message from backend if available
        const errorMessage = result.error || 'Invalid access code. Please try again.';
        setVerifyError(errorMessage);
      }
    } catch (error) {
      setVerifyError('Error verifying code: ' + error.message);
    } finally {
      setVerifying(false);
    }
  };

  const handleJoin = async () => {
    if (!roomId) {
      alert('Missing room id.');
      return;
    }

    if (!codeVerified || !verifiedAccessCodeRef.current) {
      alert('Please verify your access code first.');
      return;
    }

    setJoined(true);
    addLog('Joining...');

    const wsBaseUrl = getWebSocketUrl();
    const ws = new WebSocket(`${wsBaseUrl}/ws`);
    wsRef.current = ws;

    ws.onopen = async () => {
      ws.send(JSON.stringify({ 
        type: 'join', 
        roomId, 
        role: 'homeowner',
        accessCode: verifiedAccessCodeRef.current 
      }));
      try {
        // Don't create peer connection yet - wait for appraiser to join
        // This prevents stale connections if there's a delay
        if (!localStreamRef.current) {
          await startLocal(); // start camera immediately
        }
        // Send initial GPS location to appraiser (if they're already there)
        sendLocationUpdate();
      } catch (e) {
        addLog('getUserMedia error: ' + e.message);
      }
    };

    ws.onmessage = async (ev) => {
      const msg = JSON.parse(ev.data);

      if (msg.type === 'joined') {
        addLog('Joined as ' + msg.role);
        setJoined(true);
      }

      if (msg.type === 'peer-ready') {
        setPeerPresent(true);
        peerPresentRef.current = true;
        // When appraiser joins, recreate peer connection to ensure fresh state
        if (msg.peer === 'appraiser') {
          // Recreate peer connection to avoid stale state from long wait
          if (pcRef.current) {
            const oldState = pcRef.current.connectionState;
            if (oldState === 'new' || oldState === 'connecting' || oldState === 'disconnected' || oldState === 'failed') {
              pcRef.current = makePeerConnection();
              // Re-add local stream if available
              if (localStreamRef.current) {
                addLocalStreamToPeerConnection(pcRef.current, localStreamRef.current);
              }
            }
          } else {
            pcRef.current = makePeerConnection();
            // Add local stream if available
            if (localStreamRef.current) {
              addLocalStreamToPeerConnection(pcRef.current, localStreamRef.current);
            }
          }
          setTimeout(() => sendLocationUpdate(), 1000);
        }
      }

      if (msg.type === 'signal') {
        if (msg.data?.sdp) {
          const desc = msg.data.sdp;
          if (!pcRef.current) {
            pcRef.current = makePeerConnection();
          }
          if (!localStreamRef.current) {
            await startLocal();
          }
          
          // Ensure local stream is added to peer connection (safely)
          if (localStreamRef.current) {
            addLocalStreamToPeerConnection(pcRef.current, localStreamRef.current);
          }

          try {
            await pcRef.current.setRemoteDescription(desc);

            // apply queued ICE
            for (const c of pendingCandidatesRef.current) {
              try {
                await pcRef.current.addIceCandidate(c);
              } catch (e) {
                addLog('queued ICE error: ' + e.message);
              }
            }
            pendingCandidatesRef.current = [];
            addLog('Set remote description (' + desc.type + ').');

          if (desc.type === 'offer') {
            const answer = await pcRef.current.createAnswer();
            await pcRef.current.setLocalDescription(answer);
            wsSend({ type: 'signal', roomId, data: { sdp: pcRef.current.localDescription } });
            addLog('Created and sent answer.');
            // Status will update via onconnectionstatechange when connection is established
          }
          } catch (e) {
            addLog('Error setting remote description: ' + e.message);
            // If setting remote description fails, recreate connection
            if (e.name === 'InvalidStateError' || e.message.includes('state')) {
              addLog('Recreating peer connection due to invalid state');
              pcRef.current = makePeerConnection();
              if (localStreamRef.current) {
                addLocalStreamToPeerConnection(pcRef.current, localStreamRef.current);
              }
              // Retry setting remote description
              try {
                await pcRef.current.setRemoteDescription(desc);
                if (desc.type === 'offer') {
                  const answer = await pcRef.current.createAnswer();
                  await pcRef.current.setLocalDescription(answer);
                  wsSend({ type: 'signal', roomId, data: { sdp: pcRef.current.localDescription } });
                  addLog('Retried and sent answer.');
                }
              } catch (retryError) {
                addLog('Retry failed: ' + retryError.message);
              }
            }
          }
        }

        if (msg.data?.ice) {
          if (pcRef.current && pcRef.current.remoteDescription) {
            try {
              await pcRef.current.addIceCandidate(msg.data.ice);
            } catch (e) {
              addLog('addIceCandidate error: ' + e.message);
            }
          } else {
            pendingCandidatesRef.current.push(msg.data.ice);
          }
        }
      }

      if (msg.type === 'capture_request') {
        addLog('Capture request received from appraiser.');
        takePhotoAndUpload();
      }

      if (msg.type === 'photo_uploaded') {
        addLog('Photo successfully captured and uploaded.');
        setPhotoCaptureConfirmed(true);
        // Hide confirmation after 5 seconds
        setTimeout(() => {
          setPhotoCaptureConfirmed(false);
        }, 5000);
      }

      if (msg.type === 'peer-left') {
        setPeerPresent(false);
        peerPresentRef.current = false;
        setConnectionStatus('inactive');
      }

      if (msg.type === 'session-closed') {
        addLog('Session has been ended by the appraiser.');
        
        // Clean up and return to initial state
        if (localStreamRef.current) {
          localStreamRef.current.getTracks().forEach(track => track.stop());
        }
        if (pcRef.current) {
          pcRef.current.close();
        }
        if (wsRef.current) {
          wsRef.current.close();
        }
        
        // Clear token and show thank you message
        localStorage.removeItem('homeowner_token');
        setSessionEnded(true);
      }

      if (msg.type === 'error') {
        addLog('Error: ' + (msg.message || msg.reason || 'Unknown error'));
        // If access code error, reset to allow retry
        if (msg.message && (msg.message.includes('access code') || msg.message.includes('Authentication'))) {
          setJoined(false);
          setCodeVerified(false);
          setVerifyError('Authentication failed. Please verify your access code again.');
        }
      }

      // Handle ping/pong keepalive to prevent ALB timeout
      if (msg.type === 'ping') {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: 'pong' }));
        }
      }
    };

    ws.onclose = () => {
      setConnectionStatus('inactive');
      setPeerPresent(false);
      peerPresentRef.current = false;
    };
  };

  const handleToggleAudio = () => {
    if (remoteAudioRef.current) {
      const newMutedState = !audioMuted;
      remoteAudioRef.current.muted = newMutedState;
      setAudioMuted(newMutedState);
      addLog(newMutedState ? 'Audio muted.' : 'Audio unmuted.');
    }
  };

  const handleEndSession = () => {
    if (wsRef.current) {
      wsRef.current.close();
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
    }
    if (pcRef.current) {
      pcRef.current.close();
    }
    // Clear homeowner token
    localStorage.removeItem('homeowner_token');
    addLog('Session ended.');
    setSessionEnded(true);
  };

  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => track.stop());
      }
      // Clean up token on unmount
      localStorage.removeItem('homeowner_token');
    };
  }, []);

  return (
    <>
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
      <div className="page">
        <header>
          <img className="logo" src="/assets/logo.png" alt="Stewart Valuation Intelligence" />
          <h1>Remote Inspection</h1>
        </header>

      <main className="main">
        <div className="card" style={{width: '100%', maxWidth: '900px'}}>
          {sessionEnded ? (
            // Thank you message when session has ended
            <div style={{textAlign: 'center', padding: '40px 20px'}}>
              <div style={{
                fontSize: '48px',
                marginBottom: '20px'
              }}>
                ✓
              </div>
              <h1 style={{color: '#000', marginTop: 0, marginBottom: '16px'}}>
                Thank You
              </h1>
              <p style={{fontSize: '18px', color: '#6b7280', marginBottom: '8px'}}>
                Your session has ended.
              </p>
              <p style={{fontSize: '16px', color: '#9ca3af'}}>
                You may close this window.
              </p>
            </div>
          ) : (
            <>
              <h1 style={{color: '#000', marginTop: 0}}>Homeowner</h1>
              <p className="hint" style={{marginTop: '-6px'}}>
                {roomId ? `Room: ${roomId}` : 'No room id in URL'}
              </p>

              {!codeVerified && (
                <div className="section" style={{marginBottom: '20px', background: '#f1f5f9', padding: '20px', borderRadius: '12px'}}>
                  <h3 style={{marginTop: 0, marginBottom: '12px'}}>Enter Access Code</h3>
                  <p className="hint" style={{marginBottom: '12px'}}>
                    Please enter the 6-digit access code provided by your appraiser.
                  </p>
                  <div className="row" style={{marginBottom: '12px'}}>
                    <input
                      type="text"
                      placeholder="Enter 6-digit code"
                      value={accessCode}
                      onChange={(e) => setAccessCode(e.target.value)}
                      maxLength={6}
                      style={{flex: 1, fontSize: '18px', letterSpacing: '3px', textAlign: 'center'}}
                      disabled={verifying}
                    />
                    <button 
                      onClick={handleVerifyCode}
                      disabled={verifying || !accessCode.trim()}
                      style={{minWidth: '120px'}}
                    >
                      {verifying ? 'Verifying...' : 'Verify Code'}
                    </button>
                  </div>
                  {verifyError && (
                    <p style={{color: '#dc2626', margin: '8px 0 0', fontSize: '14px'}}>
                      {verifyError}
                    </p>
                  )}
                </div>
              )}

              {codeVerified && (
                <div style={{marginBottom: '12px', padding: '12px', background: '#dcfce7', borderRadius: '8px', color: '#166534'}}>
                  ✓ Access code verified
                </div>
              )}

              {joined && (
                <div style={{
                  marginBottom: '12px', 
                  padding: '12px', 
                  background: '#fef3c7', 
                  borderRadius: '8px', 
                  fontSize: '13px',
                  border: '1px solid #fbbf24'
                }}>
                  <strong>📍 For best GPS accuracy:</strong>
                  <ul style={{margin: '8px 0 0', paddingLeft: '20px'}}>
                    <li>Use a mobile device with GPS (phone/tablet)</li>
                    <li>Enable Location Services in device settings. Ensure this is enabled for the browser as well.</li>
                    <li>Grant "precise location" permission to browser</li>
                  </ul>
                </div>
              )}

              <div className="row" style={{margin: '8px 0 20px'}}>
                <button 
                  className={`btn ${joined ? 'on' : ''}`}
                  onClick={handleJoin}
                  disabled={joined || !codeVerified}
                >
                  {joined ? 'Joined' : 'Join Session'}
                </button>
                <button 
                  className="btn btn-secondary"
                  onClick={handleToggleAudio}
                  disabled={!joined}
                >
                  {audioMuted ? '🔇 Unmute Audio' : '🔊 Mute Audio'}
                </button>
                <button 
                  className="btn"
                  onClick={handleEndSession}
                  disabled={!joined}
                  style={{background: '#dc2626', borderColor: '#dc2626'}}
                >
                  End Session
                </button>
              </div>

              {photoCaptureConfirmed && (
                <div style={{
                  marginBottom: '12px', 
                  padding: '16px', 
                  background: '#dcfce7', 
                  borderRadius: '8px', 
                  fontSize: '16px',
                  border: '2px solid #22c55e',
                  color: '#166534',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  animation: 'fadeIn 0.3s ease-in'
                }}>
                  <div style={{fontSize: '24px'}}>✓</div>
                  <div>
                    <strong>Photo captured successfully!</strong>
                    <div style={{fontSize: '14px', marginTop: '4px', opacity: 0.8}}>
                      Your photo has been saved and sent to the appraiser.
                    </div>
                  </div>
                </div>
              )}

              <div className="video-tile">
                <h3 style={{margin: '0 0 8px'}}>Your camera (preview)</h3>
                <video 
                  ref={localVideoRef}
                  autoPlay 
                  playsInline 
                  muted
                  style={{width: '100%', borderRadius: '12px', display: 'block'}}
                />
              </div>

              <audio ref={remoteAudioRef} autoPlay muted={audioMuted} />
            </>
          )}
        </div>
      </main>
      </div>
    </>
  );
}

export default HomeownerConsole;
